/**
 * P13-V5A-R3 — the super-admin analytics consumer.
 *
 * WHAT THIS REPLACES. Every one of the five reads carried its own `.catch(() => ({ data: null }))`,
 * so a backend failure became "no data", and "no data" then became a rendered number: the
 * "Efectividad" card printed `'0%'` whenever `hasOperationalKpiData` was false, i.e. on an empty
 * partition AND on a broken backend alike. The page could not tell a real zero from an empty
 * partition from a failure, and it published a figure in all three cases. A backend that starts
 * telling the truth does not produce a truthful product while its consumer behaves like this.
 *
 * THE CONTRACT NOW CONSUMED (`P13-V5A-R3`):
 *   OK       -> a finite number (0 included)
 *   NO_DATA  -> null
 *   ERROR    -> a non-2xx; the promise rejects and the page shows its error state.
 *
 * Anything else is an INCOMPATIBLE payload and is treated as an error — never quietly downgraded to
 * "Sin datos", because a broken contract must not be able to disguise itself as a legitimate
 * absence. `metric_states` is validated against the values it describes; a state that contradicts
 * its value fails the whole payload.
 *
 * THERE IS NO FALLBACK FAMILY. `/analytics/kpis` is the canonical KPI reader; if it fails, this page
 * says so. It never re-reads `/dashboard/kpis`, which is a different producer with a different
 * authority and a different partition.
 *
 * RETIRED CARDS. "Agentes en Campo" was `usuarios_conectados`, which was a count of active user ROWS
 * — there is no presence source in MGI, so the card had no producer and is gone. "Efectividad" was
 * `efficiency_score`, an invented weighted composite; it is replaced by "Tasa de Cierre", which is
 * closed/total and is defined, bounded and falsifiable.
 */
import { ref, computed } from 'vue'
import { ApiClient } from '@/services/api/api-client'

type Tone = 'red' | 'purple' | 'blue' | 'green'

export const ANALYTICS_TRUTH_CONTRACT = 'P13-V5A-R3'
export const NO_DATA_LABEL = 'Sin datos'

export interface KpiCard {
  tone: Tone
  icon: string
  title: string
  value: number | string
  subtitle: string
  /** 'OK' | 'NO_DATA' — so a template can style an absence instead of printing a number. */
  state: 'OK' | 'NO_DATA'
}

export interface SimpleMetricItem {
  label: string
  value: number
}

/** The metrics this page publishes. A payload missing any of them is incompatible, not empty. */
const REQUIRED_METRICS = [
  'total_incidents',
  'active_incidents',
  'closed_incidents',
  'critical_incidents',
  'completion_rate'
] as const

/** OK <-> a finite number. NO_DATA <-> null/undefined. No other combination is admissible. */
export function isAdmissibleMetric(state: unknown, value: unknown): boolean {
  if (state === 'OK') return typeof value === 'number' && Number.isFinite(value)
  if (state === 'NO_DATA') return value === null || value === undefined
  return false
}

/**
 * Validate a KPI payload against the truth contract.
 * @returns the payload when it is admissible; throws otherwise.
 */
export function validateKpiPayload(payload: any): any {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Respuesta de KPIs no interpretable')
  }
  if (payload?.meta?.truth_contract !== ANALYTICS_TRUTH_CONTRACT) {
    throw new Error('Contrato de KPIs desconocido; no se puede interpretar la respuesta')
  }
  const states = payload.metric_states
  if (!states || typeof states !== 'object') {
    throw new Error('Respuesta de KPIs sin estados de métrica')
  }
  for (const key of REQUIRED_METRICS) {
    if (!isAdmissibleMetric(states[key], payload[key])) {
      throw new Error(`Métrica incompatible con su estado declarado: ${key}`)
    }
  }
  return payload
}

/** Render a metric: a real number (0 included) or an explicit absence. Never a substitute. */
export function renderMetric(value: unknown, state: unknown, suffix = ''): number | string {
  if (state === 'NO_DATA') return NO_DATA_LABEL
  return typeof value === 'number' ? `${value}${suffix}` : NO_DATA_LABEL
}

export function useSuperAdminDashboard() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  const kpis = ref<any | null>(null)
  const rutasActivas = ref<number | null>(null)
  const rutasError = ref(false)

  const productividades = ref<SimpleMetricItem[]>([])
  const zonas = ref<SimpleMetricItem[]>([])
  const departmentsError = ref(false)

  /**
   * ── W2 · REPORTING-PAYLOAD-EXCESS-API-ANALYTICS · `slaRiskPct` IS RETIRED ──
   *
   * It was derived from `metas_cumplimiento.*.cumplimiento` on `GET /api/analytics/response-time`.
   * R3 classified that whole subtree as DEFERRED_CAPABILITY (V8 SLA/automations) and the backend
   * projection now removes it. Its visible product, "SLA en Riesgo", was a compliance claim about a
   * capability the pilot does not sell, so it is removed rather than left to render "Sin datos"
   * forever — an empty card that still names SLA keeps advertising SLA (§19).
   *
   * The panel keeps its product value from an ALLOWED metric on the SAME signed route:
   * `promedio_general`, the mean resolution duration in HOURS (the producer computes
   * `AVG(EXTRACT(EPOCH FROM (closedAt - createdAt)) / 3600)` and rounds to one decimal), published
   * as "Tiempo Promedio de Resolución". `null` remains an explicit absence, never 0 h.
   *
   * Renamed from `predictive` to `derivedIndicators` (§13): nothing here forecasts anything — both
   * entries are retrospective aggregates over observed reads — and the old name was the last place
   * on this surface still implying prediction.
   */
  const derivedIndicators = ref({
    avgResolutionHours: null as number | null,
    costTrendPct: null as number | null,
    hasData: false,
    message: ''
  })

  const hasKpis = computed(() => kpis.value !== null)

  const kpiCards = computed<KpiCard[]>(() => {
    if (!kpis.value) return []
    const k = kpis.value
    const s = k.metric_states
    const card = (
      tone: Tone, icon: string, title: string, key: string, subtitle: string, suffix = ''
    ): KpiCard => ({
      tone,
      icon,
      title,
      value: renderMetric(k[key], s[key], suffix),
      subtitle: s[key] === 'NO_DATA' ? 'Métrica no calculable en esta partición' : subtitle,
      state: s[key] === 'NO_DATA' ? 'NO_DATA' : 'OK'
    })

    const cards = [
      card('red', '⚠️', 'Incidencias Activas', 'active_incidents', 'Atención inmediata'),
      card('blue', '📋', 'Incidencias Totales', 'total_incidents', 'En la partición autorizada'),
      card('purple', '🔥', 'Críticas', 'critical_incidents', 'Prioridad crítica'),
      card('green', '📈', 'Tasa de Cierre', 'completion_rate', 'Cerradas sobre el total', '%')
    ]

    // Routes belong to another domain. The card states an absence when that service did not answer
    // readably — an unreachable route service is not "zero active routes".
    const routesUnknown = rutasError.value || rutasActivas.value === null
    cards.push({
      tone: 'blue',
      icon: '🗺️',
      title: 'Rutas Activas',
      value: routesUnknown ? NO_DATA_LABEL : (rutasActivas.value as number),
      subtitle: rutasError.value ? 'Servicio de rutas no disponible' : 'Supervisión global',
      state: routesUnknown ? 'NO_DATA' : 'OK'
    })
    return cards
  })

  async function loadAll() {
    loading.value = true
    error.value = null
    rutasError.value = false
    departmentsError.value = false

    // The CANONICAL read. Deliberately NOT wrapped in a swallowing catch: if it fails, the page
    // fails visibly. No substitute family, no substitute number.
    let kpiPayload: any
    try {
      const res = await ApiClient.get<any>('/analytics/kpis')
      kpiPayload = validateKpiPayload(res?.data)
    } catch (e: any) {
      kpis.value = null
      productividades.value = []
      zonas.value = []
      derivedIndicators.value = { avgResolutionHours: null, costTrendPct: null, hasData: false, message: '' }
      error.value = e?.response?.status === 403
        ? 'No tiene autorización para consultar estos indicadores.'
        : (e?.message || 'No se pudieron cargar los indicadores')
      loading.value = false
      return
    }
    kpis.value = kpiPayload

    // Secondary panels. Each records its OWN failure; none can invent a value, and none can mask
    // the failure of another.
    const [routesRes, deptRes, respTimeRes, trendRes] = await Promise.allSettled([
      ApiClient.get<any>('/routes'),
      ApiClient.get<any>('/analytics/departments'),
      ApiClient.get<any>('/analytics/response-time'),
      ApiClient.get<any>('/analytics/trends', { params: { months: 6 } })
    ])

    if (routesRes.status === 'fulfilled') {
      const raw = (routesRes.value as any)?.data?.data ?? (routesRes.value as any)?.data
      if (Array.isArray(raw)) {
        rutasActivas.value = raw.filter((r: any) => r?.is_active !== false && r?.status !== 'PAUSADA').length
      } else if (typeof raw === 'number') {
        rutasActivas.value = raw
      } else if (typeof raw?.total === 'number') {
        rutasActivas.value = raw.total
      } else {
        rutasError.value = true          // an unreadable shape is NOT "zero routes"
        rutasActivas.value = null
      }
    } else {
      rutasError.value = true
      rutasActivas.value = null
    }

    if (deptRes.status === 'fulfilled' && Array.isArray((deptRes.value as any)?.data?.departments)) {
      const rows = (deptRes.value as any).data.departments
      // Only departments whose metric is genuinely OK enter a ranking. A NO_DATA department is
      // omitted rather than ranked as if it had scored zero.
      productividades.value = rows
        .filter((d: any) => d?.metric_states?.completion_rate === 'OK')
        .map((d: any) => ({ label: d.department_name, value: Math.round(d.completion_rate) }))
        .sort((a: SimpleMetricItem, b: SimpleMetricItem) => b.value - a.value)
        .slice(0, 5)
      // ── W2 · the second ranking is now INCIDENCE-ONLY ──
      // It ranked departments by `workload_per_member`, a workforce-capacity ratio R3 classified as
      // UNCONTRACTED_CAPABILITY and the backend projection has removed. The replacement is
      // `open_incidents` from the same signed response: how many incidences each department
      // currently has open inside the authorized partition. Nothing is divided by headcount and no
      // second source is consulted.
      //
      // A real 0 still ranks — a department with no open incidences is an OBSERVATION and belongs in
      // the list — so the admission test is the metric's own state, exactly as `completion_rate`
      // above. Only a NO_DATA row is omitted.
      zonas.value = rows
        .filter((d: any) => d?.metric_states?.open_incidents === 'OK')
        .map((d: any) => ({ label: d.department_name, value: Math.round(d.open_incidents) }))
        .sort((a: SimpleMetricItem, b: SimpleMetricItem) => b.value - a.value)
        .slice(0, 5)
    } else {
      departmentsError.value = deptRes.status !== 'fulfilled'
      productividades.value = []
      zonas.value = []
    }

    // Derived indicators exist only when their inputs really do.
    //
    // W2: this read `metas_cumplimiento.*.cumplimiento` and published `100 - avg` as "SLA en Riesgo".
    // That subtree is DEFERRED V8 capability and no longer reaches a pilot client. The allowed
    // measurement on the same signed route is `promedio_general` — mean resolution HOURS over the
    // authorized partition — and it is taken as produced, never recomputed here.
    let avgResolutionHours: number | null = null
    if (respTimeRes.status === 'fulfilled') {
      const observed = (respTimeRes.value as any)?.data?.promedio_general
      // NO_DATA, not 0 h: the producer already answers `null` when nothing finalized in the
      // partition, and a duration of zero would be a measurement nobody made.
      if (typeof observed === 'number' && Number.isFinite(observed)) {
        avgResolutionHours = observed
      }
    }

    let costTrend: number | null = null
    if (trendRes.status === 'fulfilled') {
      const rows = (trendRes.value as any)?.data?.trends
      if (Array.isArray(rows) && rows.length >= 2) {
        const first = rows[0]?.valor
        const last = rows[rows.length - 1]?.valor
        if (typeof first === 'number' && typeof last === 'number' && first > 0) {
          costTrend = Math.round(((last - first) / first) * 100)
        }
      }
    }

    derivedIndicators.value = {
      avgResolutionHours,
      costTrendPct: costTrend,
      hasData: avgResolutionHours !== null || costTrend !== null,
      message: avgResolutionHours === null && costTrend === null
        ? 'Sin lecturas suficientes para calcular indicadores derivados.'
        : ''
    }

    loading.value = false
  }

  return {
    loading,
    error,
    hasKpis,
    kpis,
    kpiCards,
    productividades,
    zonas,
    departmentsError,
    derivedIndicators,
    loadAll
  }
}
